import { motion } from "motion/react";
const googlePlayBadge = "/assets/google-play-download.png";
import { 
  Leaf, 
  BookOpen, 
  Database, 
  Camera, 
  HelpCircle,
  FileText,
  Mail,
  Shield,
  Facebook,
  Instagram,
  Linkedin,
  Twitter
} from "lucide-react";

interface FooterProps {
  setIsGetAppOpen?: (open: boolean) => void;
}

export default function Footer({ setIsGetAppOpen }: FooterProps) {
  const currentYear = new Date().getFullYear();

  const handleNavigate = (path: string) => {
    window.history.pushState(null, "", path);
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#090d16] text-gray-300 border-t border-[#1e293b] pt-6 pb-12 relative overflow-hidden print:hidden" aria-label="Global Directory Footer">
      
      {/* Subtle clean backing light */}
      <div className="absolute top-0 left-1/4 -translate-y-1/2 w-[800px] h-[350px] bg-green-500/[0.015] rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10 space-y-16">
        
        {/* Main Columns Grid */}
        <nav className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10" aria-label="Footer Directory Navigation">
          
          {/* COLUMN 1: ABOUT (3 cols) */}
          <div className="lg:col-span-3 space-y-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white">
                <Leaf className="w-4 h-4" />
              </div>
              <span className="text-lg font-black tracking-tight text-white">
                Plant Identifer
              </span>
            </div>

            <p className="text-xs sm:text-sm text-gray-400 font-medium leading-relaxed">
              Plant Identifer helps users identify plants, flowers, trees, weeds, and leaves through image analysis and educational botanical information.
            </p>

            {/* Trust badges */}
            <div className="space-y-2 pt-2 text-xs font-bold text-gray-350">
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold select-none">✓</span>
                <span>Plant Identification</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold select-none">✓</span>
                <span>Botanical Database</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold select-none">✓</span>
                <span>Educational Resource</span>
              </div>
            </div>

            {/* Google Play Download Section */}
            <div className="pt-3" id="footer-google-play-section">
              <a 
                href="https://play.google.com/store/apps/details?id=com.plant.identifer"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform duration-200 hover:scale-105 active:scale-95"
                id="google-play-footer-link"
              >
                <img 
                  src={googlePlayBadge} 
                  alt="Get it on Google Play" 
                  width={135}
                  height={40}
                  className="h-10 w-auto object-contain rounded-lg shadow-md"
                  id="google-play-badge-img"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </a>
            </div>

            {/* Social Media Links */}
            <div className="pt-4 flex items-center gap-3.5" id="footer-social-links-section">
              <a 
                href="https://www.facebook.com/Alltoolsofficial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8.5 h-8.5 rounded-xl bg-white/5 hover:bg-white/10 hover:text-emerald-400 border border-white/10 flex items-center justify-center transition-all duration-200"
                title="Facebook"
                id="footer-facebook-link"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://www.instagram.com/alltoolsofficial/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8.5 h-8.5 rounded-xl bg-white/5 hover:bg-white/10 hover:text-emerald-400 border border-white/10 flex items-center justify-center transition-all duration-200"
                title="Instagram"
                id="footer-instagram-link"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="https://www.linkedin.com/company/alltoolspro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8.5 h-8.5 rounded-xl bg-white/5 hover:bg-white/10 hover:text-emerald-400 border border-white/10 flex items-center justify-center transition-all duration-200"
                title="LinkedIn"
                id="footer-linkedin-link"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="https://x.com/alltoolsnow" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8.5 h-8.5 rounded-xl bg-white/5 hover:bg-white/10 hover:text-emerald-400 border border-white/10 flex items-center justify-center transition-all duration-200"
                title="Twitter / X"
                id="footer-twitter-link"
              >
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* COLUMN 2: TOOLS (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-xs text-white font-black uppercase tracking-wider">Tools</h2>
            <ul className="space-y-2.5 text-xs sm:text-sm font-semibold">
              <li>
                <a 
                  href="/identify-plant"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/identify-plant"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Access the interactive Plant Identification tool"
                >
                  Identify Plant
                </a>
              </li>
              <li>
                <a 
                  href="/plant-database"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/plant-database"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Browse our comprehensive Botanical Database"
                >
                  Plant Database
                </a>
              </li>
              <li>
                <a 
                  href="/plant-care-guides"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/plant-care-guides"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read professional Plant Care Guides"
                >
                  Plant Care Guides
                </a>
              </li>
              <li>
                <a 
                  href="/common-plant-diseases"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/common-plant-diseases"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Diagnose and treat common plant diseases"
                >
                  Common Plant Diseases
                </a>
              </li>

            </ul>
          </div>

          {/* COLUMN 3: RESOURCES (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-xs text-white font-black uppercase tracking-wider">Resources</h2>
            <ul className="space-y-2.5 text-xs sm:text-sm font-semibold">
              <li>
                <a 
                  href="/about"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/about"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Learn more about us and our mission"
                >
                  About Us
                </a>
              </li>
              <li>
                <a 
                  href="/#features"
                  onClick={(e) => { 
                    e.preventDefault(); 
                    if (window.location.pathname !== "/") {
                      window.history.pushState(null, "", "/#features");
                      window.dispatchEvent(new Event("popstate"));
                    } else {
                      document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
                    }
                  }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Explore features of Plant Identifier"
                >
                  Features
                </a>
              </li>
              <li>
                <a 
                  href="/#how-it-works"
                  onClick={(e) => { 
                    e.preventDefault(); 
                    if (window.location.pathname !== "/") {
                      window.history.pushState(null, "", "/#how-it-works");
                      window.dispatchEvent(new Event("popstate"));
                    } else {
                      document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
                    }
                  }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="See how Plant Identifier works"
                >
                  How It Works
                </a>
              </li>
              <li>
                <a 
                  href="/blog/page/1"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/blog/page/1"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our botanical blog"
                >
                  Blog
                </a>
              </li>
              <li>
                <a 
                  href="/plant-care-tips"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/plant-care-tips"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Explore essential plant care tips"
                >
                  Plant Care Tips
                </a>
              </li>
              <li>
                <a 
                  href="/gardening-guides"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/gardening-guides"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Explore interactive gardening guides"
                >
                  Gardening Guides
                </a>
              </li>
              <li>
                <a 
                  href="/faq"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/faq"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our frequently asked questions"
                >
                  FAQ
                </a>
              </li>
            </ul>
          </div>

          {/* COLUMN 4: LEGAL (3 cols) */}
          <div className="lg:col-span-3 space-y-4">
            <h2 className="text-xs text-white font-black uppercase tracking-wider">Legal & Compliance</h2>
            <ul className="space-y-2.5 text-xs sm:text-sm font-semibold">
              <li>
                <a 
                  href="/privacy-policy"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/privacy-policy"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our Privacy Policy"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a 
                  href="/terms-of-service"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/terms-of-service"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our Terms of Service"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a 
                  href="/disclaimer"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/disclaimer"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our botanical and medical disclaimer"
                >
                  Disclaimer
                </a>
              </li>
              <li>
                <a 
                  href="/cookie-policy"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/cookie-policy"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our Cookie Policy"
                >
                  Cookie Policy
                </a>
              </li>
              <li>
                <a 
                  href="/editorial-policy"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/editorial-policy"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our Editorial Policy"
                >
                  Editorial Policy
                </a>
              </li>
              <li>
                <a 
                  href="/data-sources"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/data-sources"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Explore our database Data Sources"
                >
                  Data Sources
                </a>
              </li>
              <li>
                <a 
                  href="/attribution"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/attribution"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Read our taxonomic Data Attribution policy"
                >
                  Data Attribution
                </a>
              </li>
              <li>
                <a 
                  href="/contact"
                  onClick={(e) => { e.preventDefault(); handleNavigate("/contact"); }} 
                  className="text-gray-400 hover:text-green-400 hover:underline transition-colors text-left block"
                  aria-label="Contact our customer service team"
                >
                  Contact Us
                </a>
              </li>

            </ul>
          </div>

        </nav>

        {/* BOTTOM COPYRIGHT & LEGAL METADATA BAR */}
        <div className="border-t border-gray-900 pt-8 flex flex-col md:flex-row items-center justify-between gap-6 text-[11px] text-gray-500 font-semibold text-center md:text-left">
          <div className="space-y-2">
            <span className="block text-gray-450 text-xs font-bold">© {currentYear} Plant Identifer. All Rights Reserved.</span>
            <p className="text-[10px] text-gray-650 leading-normal max-w-4xl">
              Plant taxonomy, classification, and care databases are compiled from trusted public botanical records, public-domain species checklists, and expert horticultural indexes. Educational purposes only. Plant identification results should be verified before medical, agricultural, or safety-related decisions are executed.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3.5 text-xs font-bold shrink-0 select-none justify-center">
            <span className="text-gray-750 hidden md:inline">|</span>
            <a 
              href="/privacy-policy" 
              onClick={(e) => { e.preventDefault(); handleNavigate("/privacy-policy"); }} 
              className="text-gray-500 hover:text-white transition-colors block font-mono text-[10px]"
              aria-label="Read our Privacy Policy"
            >
              Privacy
            </a>
            <span className="text-gray-800 select-none">•</span>
            <a 
              href="/terms-of-service" 
              onClick={(e) => { e.preventDefault(); handleNavigate("/terms-of-service"); }} 
              className="text-gray-500 hover:text-white transition-colors block font-mono text-[10px]"
              aria-label="Read our Terms of Service"
            >
              Terms
            </a>
            <span className="text-gray-805 select-none">•</span>
            <a 
              href="/data-sources" 
              onClick={(e) => { e.preventDefault(); handleNavigate("/data-sources"); }} 
              className="text-gray-500 hover:text-white transition-colors block font-mono text-[10px]"
              aria-label="Explore our database Data Sources"
            >
              Sources
            </a>
            <span className="text-gray-805 select-none">•</span>
            <a 
              href="/attribution" 
              onClick={(e) => { e.preventDefault(); handleNavigate("/attribution"); }} 
              className="text-gray-500 hover:text-white transition-colors block font-mono text-[10px]"
              aria-label="Review database Attribution details"
            >
              Attribution
            </a>
            <span className="text-gray-805 select-none">•</span>
            <a 
              href="/contact" 
              onClick={(e) => { e.preventDefault(); handleNavigate("/contact"); }} 
              className="text-gray-500 hover:text-white transition-colors block font-mono text-[10px]"
              aria-label="Get in touch with us"
            >
              Contact
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}
